import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useState, useRef } from "react";
import { clamp } from "lodash";
import { Tooltip } from "../../Tooltip/index.js";
import { withThemeAndSize } from "../../withThemeAndSize/index.js";
import { cn as clsx } from "../../../helpers/react.js";
import { isFiniteNumber, isFunction } from "../../../helpers/utils.js";
import '../../../assets/index2.css';const progressContainer = "_progressContainer_1t5c7_1";
const videoTimeline = "_videoTimeline_1t5c7_15";
const videoSegment = "_videoSegment_1t5c7_21";
const primaryBk = "_primaryBk_1t5c7_28";
const progressBar = "_progressBar_1t5c7_41";
const provisionalProgressBar = "_provisionalProgressBar_1t5c7_74";
const segmentBar = "_segmentBar_1t5c7_95";
const segmentBar1 = "_segmentBar1_1t5c7_102";
const segmentBar2 = "_segmentBar2_1t5c7_114";
const hasLeftSegment = "_hasLeftSegment_1t5c7_114";
const hasRightSegment = "_hasRightSegment_1t5c7_125";
const tooltip = "_tooltip_1t5c7_137";
const timelineContainer = "_timelineContainer_1t5c7_146";
const s = {
  progressContainer,
  videoTimeline,
  videoSegment,
  primaryBk,
  progressBar,
  provisionalProgressBar,
  segmentBar,
  segmentBar1,
  segmentBar2,
  hasLeftSegment,
  hasRightSegment,
  tooltip,
  timelineContainer
};
const getValidSegmentNumber = (value) => {
  if (!isFiniteNumber(value)) {
    return null;
  }
  if (clamp(value, 0, 1) !== value) {
    return null;
  }
  return value;
};
const calcProgress = (ev, ref) => {
  if (!ref.current) {
    return null;
  }
  const rect = ref.current.getBoundingClientRect();
  return clamp((ev.clientX - rect.left) / rect.width, 0, 1);
};
const getTooltipToProgressRatio = (refToUse, tooltipRef) => {
  const progressEl = refToUse.current;
  const tooltipEl = tooltipRef.current;
  if (!progressEl || !tooltipEl) {
    return 0;
  }
  const progressRect = progressEl.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  return tooltipRect.width / progressRect.width * 0.5;
};
const _VideoTimeline = withThemeAndSize(
  forwardRef(
    ({
      children,
      className,
      progressValue,
      onClick,
      onProgressClick,
      onProgressHover,
      onMouseMove,
      onMouseEnter,
      onMouseLeave,
      renderTooltip,
      variant: _variant,
      segmentStart,
      segmentEnd,
      ...rest
    }, forwardedRef) => {
      const [provisionalProgress, setProvisionalProgress] = useState(null);
      const progress = isFiniteNumber(progressValue) ? clamp(progressValue, 0, 1) : 0;
      const ref = useRef(null);
      const refToUse = forwardedRef && !isFunction(forwardedRef) ? forwardedRef : ref;
      const tooltipRef = useRef(null);
      const onClickHandler = (ev) => {
        onClick == null ? void 0 : onClick(ev);
        const value = calcProgress(ev, refToUse);
        if (value !== null) {
          onProgressClick == null ? void 0 : onProgressClick(value);
        }
      };
      const onHoverHandler = (ev) => {
        onMouseMove == null ? void 0 : onMouseMove(ev);
        const value = calcProgress(ev, refToUse);
        setProvisionalProgress(value);
        onProgressHover == null ? void 0 : onProgressHover(value);
      };
      const onMouseEnterHandler = (ev) => {
        onMouseEnter == null ? void 0 : onMouseEnter(ev);
        const value = calcProgress(ev, refToUse);
        setProvisionalProgress(value);
        onProgressHover == null ? void 0 : onProgressHover(value);
      };
      const onMouseLeaveHandler = (ev) => {
        setProvisionalProgress(null);
        onMouseLeave == null ? void 0 : onMouseLeave(ev);
        onProgressHover == null ? void 0 : onProgressHover(null);
      };
      const tooltipToProgressRatio = getTooltipToProgressRatio(refToUse, tooltipRef);
      const variantClass = _variant === "segment" ? s.videoSegment : s.videoTimeline;
      const shouldRenderSegment = isFiniteNumber(segmentStart) || isFiniteNumber(segmentEnd);
      const validSegmentStart = getValidSegmentNumber(segmentStart);
      const validSegmentEnd = getValidSegmentNumber(segmentEnd);
      const segmentStyle = {
        left: validSegmentStart ? validSegmentStart * 100 + "%" : 0,
        right: validSegmentEnd ? (1 - validSegmentEnd) * 100 + "%" : 0
      };
      return /* @__PURE__ */ jsxs(
        "section",
        {
          ...rest,
          className: clsx(s.progressContainer, variantClass, className),
          ref: refToUse,
          onClick: onClickHandler,
          onMouseMove: onHoverHandler,
          onMouseEnter: onMouseEnterHandler,
          onMouseLeave: onMouseLeaveHandler,
          children: [
            /* @__PURE__ */ jsxs("div", { className: clsx(s.primaryBk, variantClass), children: [
              shouldRenderSegment && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("div", { className: clsx(s.segmentBar, s.segmentBar1), style: segmentStyle }),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: clsx(s.segmentBar, s.segmentBar2, {
                      [s.hasLeftSegment]: validSegmentStart !== null,
                      [s.hasRightSegment]: validSegmentEnd !== null
                    }),
                    style: segmentStyle
                  }
                )
              ] }),
              provisionalProgress && /* @__PURE__ */ jsx(
                "div",
                {
                  className: clsx(s.provisionalProgressBar, variantClass),
                  style: { width: provisionalProgress * 100 + "%" }
                }
              ),
              /* @__PURE__ */ jsx("div", { className: clsx(s.progressBar, variantClass), style: { width: progress * 100 + "%" } })
            ] }),
            /* @__PURE__ */ jsx(
              "div",
              {
                ref: tooltipRef,
                className: clsx(s.tooltip, Tooltip.CONTENT_CLASS_NAME),
                style: {
                  visibility: provisionalProgress === null ? "hidden" : "visible",
                  left: clamp(provisionalProgress || 0, tooltipToProgressRatio, 1 - tooltipToProgressRatio) * 100 + "%"
                },
                children: renderTooltip == null ? void 0 : renderTooltip(provisionalProgress || 0)
              }
            )
          ]
        }
      );
    }
  )
);
const VideoTimelineContainer = withThemeAndSize(
  forwardRef(({ children, className, ...rest }, ref) => {
    return /* @__PURE__ */ jsx("div", { ...rest, ref, className: clsx(s.timelineContainer, className), children });
  })
);
const VideoTimeline = Object.assign(_VideoTimeline, {
  Container: VideoTimelineContainer
});
export {
  VideoTimeline
};
